import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginated } from '@manpower/shared';
import { api } from '@/lib/api';
import { formatRelative } from '@/lib/labels';
import { Icon, Spinner, cx } from '@/components/primitives';

interface NotificationRow {
  id: string;
  subject: string | null;
  body: string;
  actionUrl: string | null;
  readAt: string | null;
  createdAt: string;
}

/**
 * How often the badge re-checks.
 *
 * There is no websocket in this stack, so the count is polled. A minute is
 * frequent enough that an interview reschedule reaches a recruiter while they
 * are still at their desk, and slow enough to cost nothing on a phone —
 * TanStack pauses the interval entirely while the tab is in the background.
 */
const POLL_MS = 60_000;

/**
 * The notification bell.
 *
 * The API has had `GET /notifications`, `unread-count`, `:id/read` and
 * `read-all` for as long as the module has existed — the unread-count endpoint's
 * own summary reads "Badge count for the notification bell". There was no bell.
 * Every portal header went straight from the brand to the profile menu, so an
 * employer had no way to learn that their verification had been approved, or a
 * candidate that an interview had moved, without reloading the right page.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: ({ signal }) => api.get<{ count: number }>('/notifications/unread-count', undefined, signal),
    refetchInterval: POLL_MS,
    // The app disables focus refetching globally, which is right for a job list
    // and wrong for a badge: coming back to the tab is exactly when someone
    // wants to know what arrived while they were away.
    refetchOnWindowFocus: true,
    // A failed badge is not worth an error state in the header.
    retry: false,
  });

  // Only fetched once the panel is opened — the header itself needs the count,
  // not the contents, and most sessions never open it.
  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: ({ signal }) =>
      api.get<Paginated<NotificationRow>>('/notifications', { page: 1, limit: 15 }, signal),
    enabled: open,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`, {}),
    onSuccess: invalidate,
  });

  const markAllRead = useMutation({
    mutationFn: () => api.patch('/notifications/read-all', {}),
    onSuccess: invalidate,
  });

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      wrapper.current?.querySelector('button')?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const count = unread.data?.count ?? 0;
  const rows = list.data?.data ?? [];

  const openRow = (row: NotificationRow) => {
    if (!row.readAt) markRead.mutate(row.id);
    setOpen(false);
    // Internal paths only. `actionUrl` is written by the server, but routing a
    // bare string through `navigate` would follow an absolute URL off-site if
    // one ever landed in the column.
    if (row.actionUrl?.startsWith('/')) navigate(row.actionUrl);
  };

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        className="relative grid h-10 w-10 place-items-center rounded-md text-ink-600 transition-colors hover:bg-ink-100 hover:text-ink-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <Icon name="bell" size="lg" />
        {count > 0 && (
          <span
            // Not the only signal: the button's accessible name carries the
            // count too, so it never depends on seeing a red dot.
            aria-hidden="true"
            className="absolute right-1 top-1 min-w-4 rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white"
          >
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          className="absolute right-0 top-full z-30 mt-1 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-ink-200 motion-safe:animate-toast-in"
        >
          <header className="flex items-center justify-between gap-2 border-b border-ink-200 px-3 py-2">
            <h2 className="text-sm font-semibold text-ink-900">Notifications</h2>
            {count > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                className="text-xs font-medium text-brand-700 underline-offset-2 hover:underline disabled:opacity-50 focus-visible:outline focus-visible:outline-2"
              >
                Mark all read
              </button>
            )}
          </header>

          <div className="max-h-96 overflow-y-auto">
            {list.isPending ? (
              <p className="flex items-center justify-center gap-2 px-3 py-8 text-sm text-ink-500">
                <Spinner /> Loading…
              </p>
            ) : list.isError ? (
              <p className="px-3 py-8 text-center text-sm text-ink-500">
                Could not load notifications.
              </p>
            ) : rows.length === 0 ? (
              <div className="px-3 py-8 text-center">
                <span className="mx-auto grid h-10 w-10 place-items-center rounded-full bg-brand-50 text-brand-600">
                  <Icon name="bell" size="lg" />
                </span>
                <p className="mt-2 text-sm font-medium text-ink-900">You are all caught up</p>
                <p className="mt-0.5 text-xs text-ink-500">
                  Updates about your applications and approvals appear here.
                </p>
              </div>
            ) : (
              <ul>
                {rows.map((row) => (
                  <li key={row.id} className="border-b border-ink-100 last:border-b-0">
                    <button
                      type="button"
                      onClick={() => openRow(row)}
                      className={cx(
                        'flex w-full gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-ink-50',
                        'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2',
                        !row.readAt && 'bg-brand-50/60',
                      )}
                    >
                      {/* Unread is carried by weight and the dot together, never
                          by the tint alone — a wash this light is invisible on a
                          dim phone screen in a factory. */}
                      <span
                        aria-hidden="true"
                        className={cx(
                          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                          row.readAt ? 'bg-transparent' : 'bg-brand-600',
                        )}
                      />
                      <span className="min-w-0 flex-1">
                        <span
                          className={cx(
                            'block text-sm text-ink-900',
                            row.readAt ? 'font-normal' : 'font-semibold',
                          )}
                        >
                          {row.subject || row.body}
                        </span>
                        {row.subject && (
                          <span className="mt-0.5 block text-xs text-ink-600">{row.body}</span>
                        )}
                        <span className="mt-1 block text-xs text-ink-400">
                          {formatRelative(row.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
